import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Users, Award, Landmark, Stethoscope, HeartHandshake, Briefcase, Store, FileCheck, Building2 } from "lucide-react";
import { COMPANY, INDUSTRIES, STATS, PILLARS } from "@/lib/content";
import { INSIGHTS } from "@/lib/insights";
import LogoMarquee from "@/components/site/LogoMarquee";
import TestimonialsCarousel from "@/components/site/TestimonialsCarousel";
import ServicesTabs from "@/components/site/ServicesTabs";
import Reveal from "@/components/site/Reveal";
import StatCounter from "@/components/site/StatCounter";

const HERO_IMG = "https://images.unsplash.com/photo-1557804506-669a67965ba0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwxfHxjb3Jwb3JhdGUlMjBvZmZpY2UlMjBtZWV0aW5nfGVufDB8fHx8MTc4MzczNDE5MHww&ixlib=rb-4.1.0&q=85";

const TRUST = [
  { icon: ShieldCheck, label: "Compliance-First", copy: "Federal, state, and local employment law rigor built into every engagement." },
  { icon: Landmark, label: "GovCon-Ready", copy: "SAM.gov registered, capability statement prepared, and proposal-ready." },
  { icon: Users, label: "Executive HR Bench", copy: "Access CHRO-caliber leadership without the full-time overhead." },
  { icon: Award, label: "Outcomes-Driven", copy: "We measure our work in reduced risk, better hires, and stronger cultures." },
];

const INDUSTRY_ICONS = { healthcare: Stethoscope, nonprofit: HeartHandshake, government: Landmark, "small-business": Store, "professional-services": Briefcase };

export default function HomePage() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative bg-charcoal text-white overflow-hidden">
        <div className="absolute inset-0 opacity-25">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-charcoal via-charcoal/90 to-charcoal/40" />
        </div>
        <div className="grain-overlay" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12 py-24 lg:py-36">
          <div className="max-w-4xl fade-in-up">
            <div className="overline mb-6">HR Consulting &middot; {COMPANY.location}</div>
            <h1 className="font-heading text-4xl md:text-6xl lg:text-7xl font-semibold tracking-tight leading-[1.02]">
              Strategic HR Solutions That Help Organizations{" "}
              <span className="text-gold">Build Stronger Workforces</span>
            </h1>
            <p className="mt-8 text-base md:text-lg text-white/80 max-w-2xl leading-relaxed">
              Groman Associates delivers executive-level HR leadership, compliance rigor, recruiting, and workforce strategy — without the cost of a full-time in-house HR department. Trusted by growing organizations, healthcare systems, nonprofits, and government contractors.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/contact" data-testid="hero-cta-consultation" className="inline-flex items-center gap-2 bg-gold text-white px-6 py-4 text-sm font-medium tracking-wide hover:bg-gold-hover transition-colors">
                Request a Consultation <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/services" data-testid="hero-cta-services" className="inline-flex items-center gap-2 border border-white/30 text-white px-6 py-4 text-sm font-medium tracking-wide hover:border-gold hover:text-gold transition-colors">
                Explore Our Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* TRUSTED-BY MARQUEE */}
      <LogoMarquee />

      {/* THREE PILLARS with images */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 lg:py-28">
        <Reveal>
          <div className="overline mb-4">01 &mdash; Capabilities</div>
          <h2 className="font-heading text-3xl md:text-5xl font-semibold tracking-tight text-charcoal max-w-3xl leading-tight">
            Three pillars of a stronger workforce.
          </h2>
          <p className="mt-6 text-charcoal-soft text-base md:text-lg leading-relaxed max-w-2xl">
            We built our firm on the disciplines that matter most. Practical compliance, sharp recruiting, and the operational knowledge to win government contracts.
          </p>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
          {PILLARS.map((p, i) => (
            <Reveal key={p.slug} delay={i * 0.08}>
              <Link
                to={p.slug === "government-contracting" ? "/government-contracting" : "/services"}
                data-testid={`pillar-card-${p.slug}`}
                className="group block border border-gray-200 bg-white hover:border-gold transition-colors h-full"
              >
                <div className="aspect-[4/3] overflow-hidden bg-offwhite">
                  <img
                    src={p.image}
                    alt={p.label}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="p-8">
                  <div className="font-mono text-xs tracking-[0.24em] uppercase text-gold mb-4">0{i + 1}</div>
                  <h3 className="font-heading text-xl md:text-2xl font-medium text-charcoal leading-snug group-hover:text-gold transition-colors">{p.label}</h3>
                  <p className="mt-4 text-charcoal-soft leading-relaxed">{p.copy}</p>
                  <div className="mt-8 inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.24em] text-charcoal group-hover:text-gold transition-colors">
                    Learn more <ArrowRight className="w-3.5 h-3.5" />
                  </div>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* TRUST STRIP (icons) */}
      <section className="border-y border-gray-200 bg-offwhite">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {TRUST.map((t, i) => (
            <Reveal key={t.label} delay={i * 0.05}>
              <div className="flex gap-4 items-start">
                <div className="w-11 h-11 bg-charcoal grid place-items-center shrink-0">
                  <t.icon className="w-5 h-5 text-gold" />
                </div>
                <div>
                  <div className="font-heading text-charcoal font-medium">{t.label}</div>
                  <p className="text-sm text-charcoal-soft mt-1 leading-relaxed">{t.copy}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* TABBED SERVICES */}
      <ServicesTabs />

      {/* STATS band */}
      <section className="bg-charcoal text-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 lg:py-24">
          <div className="grid lg:grid-cols-12 gap-12 mb-12">
            <div className="lg:col-span-6">
              <div className="overline text-gold mb-4">By the Numbers</div>
              <h2 className="font-heading text-3xl md:text-5xl font-semibold tracking-tight leading-tight">Executive HR experience, quantified.</h2>
            </div>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-0 border-t border-white/10">
            {STATS.map((s, i) => (
              <div key={s.label} className={`p-10 border-b border-white/10 lg:border-b-0 ${i !== STATS.length - 1 ? "lg:border-r border-white/10" : ""}`}>
                <StatCounter value={s.value} suffix={s.suffix} />
                <div className="mt-4 text-sm text-white/60 leading-relaxed">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GOVCON BAND */}
      <section className="bg-white border-b border-gray-200">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 lg:py-24 grid lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-7">
            <div className="overline mb-6">02 &mdash; Government Contracting</div>
            <h2 className="font-heading text-3xl md:text-5xl font-semibold tracking-tight leading-tight text-charcoal">Federal, state, and local agencies choose partners who show up prepared.</h2>
            <p className="mt-6 text-charcoal-soft text-base md:text-lg leading-relaxed max-w-2xl">
              Groman Associates supports government contracts with administrative services, HR support, program operations, and workforce management. Capability statement, NAICS codes, and past performance available on request.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/government-contracting" data-testid="govcon-band-cta" className="inline-flex items-center gap-2 bg-gold text-white px-6 py-4 text-sm font-medium hover:bg-gold-hover transition-colors">
                View Capability Statement <FileCheck className="w-4 h-4" />
              </Link>
              <Link to="/contact" className="inline-flex items-center gap-2 border border-charcoal text-charcoal px-6 py-4 text-sm hover:bg-charcoal hover:text-white transition-colors">Submit RFP Inquiry</Link>
            </div>
          </Reveal>
          <Reveal className="lg:col-span-5" delay={0.15}>
            <div className="grid grid-cols-2 border border-gray-200">
              {[
                { k: "NAICS", v: "541612" },
                { k: "UEI", v: "Available" },
                { k: "CAGE", v: "Available" },
                { k: "SAM.gov", v: "Registered" },
              ].map((x) => (
                <div key={x.k} className="p-8 border-b border-r border-gray-200 last:border-r-0 [&:nth-child(n+3)]:border-b-0 [&:nth-child(2n)]:border-r-0">
                  <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-charcoal-soft">{x.k}</div>
                  <div className="mt-3 font-heading text-xl text-charcoal">{x.v}</div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 lg:py-28">
        <Reveal>
          <div className="overline mb-4">03 &mdash; Industries Served</div>
          <h2 className="font-heading text-3xl md:text-5xl font-semibold tracking-tight text-charcoal max-w-3xl leading-tight">Deep sector expertise where compliance and people intersect.</h2>
        </Reveal>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 grid-borders">
          {INDUSTRIES.map((ind, i) => {
            const Icon = INDUSTRY_ICONS[ind.slug] || Building2;
            return (
              <Reveal key={ind.slug} delay={i * 0.05}>
                <Link to="/industries" data-testid={`industry-card-${ind.slug}`} className="group p-8 bg-white hover:bg-charcoal transition-colors h-full block">
                  <Icon className="w-6 h-6 text-charcoal group-hover:text-gold transition-colors" />
                  <div className="mt-8 font-heading text-lg font-medium text-charcoal group-hover:text-white transition-colors">{ind.name}</div>
                  <p className="mt-2 text-xs text-charcoal-soft group-hover:text-white/70 leading-relaxed transition-colors">{ind.copy}</p>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <TestimonialsCarousel />

      {/* INSIGHTS */}
      <section id="insights" className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 lg:py-28">
        <div className="flex items-end justify-between mb-14 flex-wrap gap-6">
          <div>
            <div className="overline mb-4">04 &mdash; Groman Insights</div>
            <h2 className="font-heading text-3xl md:text-5xl font-semibold tracking-tight text-charcoal max-w-2xl leading-tight">
              Stay sharp on strategy.
            </h2>
            <p className="mt-4 text-charcoal-soft max-w-2xl">Get the latest on compliance changes, workforce planning, and government contracting. No noise, just substance.</p>
          </div>
          <Link
            to="/insights"
            data-testid="insights-view-all"
            className="inline-flex items-center gap-2 border border-charcoal text-charcoal px-5 py-3 text-sm font-medium hover:bg-charcoal hover:text-white transition-colors"
          >
            View all insights <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {INSIGHTS.map((p, i) => (
            <Reveal key={p.slug} delay={i * 0.08}>
              <Link
                to={`/insights/${p.slug}`}
                data-testid={`insight-${p.slug}`}
                className="border border-gray-200 bg-white group hover:border-gold transition-colors h-full flex flex-col"
              >
                <div className="aspect-[16/9] overflow-hidden bg-offwhite">
                  <img src={p.thumbnail || p.image} alt="" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <div className="p-8 flex-1 flex flex-col">
                  <div className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.24em] text-charcoal-soft">
                    <span className="text-gold">{p.category}</span><span>·</span><span>{p.read}</span>
                  </div>
                  <h3 className="mt-5 font-heading text-lg font-medium text-charcoal leading-snug group-hover:text-gold transition-colors">{p.title}</h3>
                  <p className="mt-3 text-sm text-charcoal-soft leading-relaxed">{p.excerpt}</p>
                  <div className="mt-8 inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.24em] text-charcoal group-hover:text-gold transition-colors">Read insight <ArrowRight className="w-3.5 h-3.5" /></div>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-charcoal text-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 lg:py-28 grid lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-8">
            <div className="overline text-gold mb-4">Ready to Strengthen Your Workforce</div>
            <h2 className="font-heading text-3xl md:text-6xl font-semibold tracking-tight leading-[1.05]">
              A conversation costs nothing.<br />
              <span className="text-gold">A compliance gap costs everything.</span>
            </h2>
            <p className="mt-6 text-white/70 max-w-xl leading-relaxed">Let us build a plan that fits your organization. Book a call and we'll tell you exactly where to start.</p>
          </div>
          <div className="lg:col-span-4 flex lg:justify-end">
            <Link to="/contact" data-testid="footer-cta-consultation" className="inline-flex items-center gap-2 bg-gold text-white px-8 py-5 text-sm font-medium tracking-wide hover:bg-gold-hover transition-colors">
              Request a Consultation <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
